
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ChannelCard } from "./ChannelCard";
import { CreateChannelDialog } from "./CreateChannelDialog";
import { EditChannelDialog } from "./EditChannelDialog";
import { DeleteChannelDialog } from "./DeleteChannelDialog";
import { useChannelsList } from "./useChannelsList";
import { createChannel, updateChannel, deleteChannel } from "./ChannelActions";
import { useChannelDialogs } from "./useChannelDialogs";

type ChannelsListContainerProps = {
  groupId: string;
  userRole: string;
};

export function ChannelsListContainer({ groupId, userRole }: ChannelsListContainerProps) {
  const { channels, loading, fetchChannels } = useChannelsList(groupId);
  const { user } = useAuth();
  
  const {
    // Create dialog state
    isCreateChannelDialogOpen,
    setIsCreateChannelDialogOpen,
    newChannelName,
    setNewChannelName,
    newChannelDescription,
    setNewChannelDescription,
    isAnnouncementChannel,
    setIsAnnouncementChannel,
    isTemporaryChannel,
    setIsTemporaryChannel,
    autoDeleteAfter,
    setAutoDeleteAfter,
    
    // Edit dialog state
    isEditDialogOpen,
    setIsEditDialogOpen,
    channelIdToEdit,
    editedChannelName,
    setEditedChannelName,
    editedChannelDescription,
    setEditedChannelDescription,
    editedIsAnnouncement,
    setEditedIsAnnouncement,
    editedIsTemporary,
    setEditedIsTemporary,
    editedAutoDeleteAfter,
    setEditedAutoDeleteAfter,
    
    // Delete dialog state
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    channelIdToDelete,
    
    // Helper functions
    resetCreateFormState,
    handleOpenEditDialog,
    handleOpenDeleteDialog
  } = useChannelDialogs(channels);

  const handleCreateChannel = async () => {
    if (!user) {
      return;
    }

    const success = await createChannel(
      groupId,
      {
        name: newChannelName,
        description: newChannelDescription,
        isAnnouncement: isAnnouncementChannel,
        isTemporary: isTemporaryChannel,
        autoDeleteAfter: autoDeleteAfter
      },
      user.id
    );

    if (success) {
      resetCreateFormState();
      setIsCreateChannelDialogOpen(false);
      fetchChannels();
    }
  };

  const handleEditChannel = async () => {
    if (!channelIdToEdit) return;
    
    const success = await updateChannel(
      channelIdToEdit,
      {
        name: editedChannelName,
        description: editedChannelDescription,
        isAnnouncement: editedIsAnnouncement,
        isTemporary: editedIsTemporary,
        autoDeleteAfter: editedAutoDeleteAfter
      }
    );

    if (success) {
      setIsEditDialogOpen(false);
      fetchChannels();
    }
  };

  const handleDeleteChannel = async () => {
    if (!channelIdToDelete) return;
    
    const success = await deleteChannel(channelIdToDelete);

    if (success) {
      setIsDeleteDialogOpen(false);
      fetchChannels();
    }
  };

  if (loading) {
    return <div className="text-center py-6">Loading channels...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Channels</h2>
        {userRole === "admin" && (
          <Button onClick={() => setIsCreateChannelDialogOpen(true)}>
            Create Channel
          </Button>
        )}
      </div>

      {channels.length > 0 ? (
        <ul className="space-y-2">
          {channels.map((channel) => (
            <ChannelCard
              key={channel.id}
              channel={channel}
              userRole={userRole}
              onEditChannel={handleOpenEditDialog}
              onDeleteChannel={handleOpenDeleteDialog}
            />
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground">No channels yet.</p>
      )}

      {/* Create Channel Dialog */}
      <CreateChannelDialog
        isOpen={isCreateChannelDialogOpen}
        onClose={() => setIsCreateChannelDialogOpen(false)}
        onCreateChannel={handleCreateChannel}
        channelName={newChannelName}
        setChannelName={setNewChannelName}
        channelDescription={newChannelDescription}
        setChannelDescription={setNewChannelDescription}
        isAnnouncement={isAnnouncementChannel}
        setIsAnnouncement={setIsAnnouncementChannel}
        isTemporary={isTemporaryChannel}
        setIsTemporary={setIsTemporaryChannel}
        autoDeleteAfter={autoDeleteAfter}
        setAutoDeleteAfter={setAutoDeleteAfter}
      />

      {/* Edit Channel Dialog */}
      <EditChannelDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onEditChannel={handleEditChannel}
        channelName={editedChannelName}
        setChannelName={setEditedChannelName}
        channelDescription={editedChannelDescription}
        setChannelDescription={setEditedChannelDescription}
        isAnnouncement={editedIsAnnouncement}
        setIsAnnouncement={setEditedIsAnnouncement}
        isTemporary={editedIsTemporary}
        setIsTemporary={setEditedIsTemporary}
        autoDeleteAfter={editedAutoDeleteAfter}
        setAutoDeleteAfter={setEditedAutoDeleteAfter}
      />

      {/* Delete Channel Dialog */}
      <DeleteChannelDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDeleteChannel={handleDeleteChannel}
      />
    </div>
  );
}
